/*
Muestra e imprime el contrato de compraventa.
Autor: Benjamín León
Fecha: 2025-02-27
*/

import { useEffect, useState } from 'react'

import { useAuth } from '../hooks/useAuth'
import { Articles, Delegate } from '../interfaces/OrganizationDto'
import useOrganizationPoster from '../hooks/useOrganizationPoster'
import { getDateFromDb, formatWithThousandSep } from '../utils/utils'

import { Info } from './MessageInfo'
import { FormFields, InputFieldProps } from './FormFields'
import DisplayError from './DisplayError'
import { ButtonAction } from './Buttons'

interface FormData {
  firstname1: string
  lastname1: string
  nin1: string
  firstname2: string
  lastname2: string
  nin2: string

  date: string
  notary: string
  municipality: string

  consignmentMinimum: string
  consignmentFee: string
  consignmentCharge: string
  consignmentCity: string
}
const formDataIni: FormData = {
  firstname1: '',
  lastname1: '',
  nin1: '',
  firstname2: '',
  lastname2: '',
  nin2: '',
  date: '',
  notary: '',
  municipality: '',
  consignmentMinimum: '',
  consignmentFee: '',
  consignmentCharge: '',
  consignmentCity: '',
}

const OrganizationDetail: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string>('')
  const { userData, setUserData } = useAuth()
  const [formData, setFormData] = useState<FormData>(formDataIni)
  const [disabled, setDisabled] = useState<boolean>(true)
  const { organizationDelegatesCrea, organizationConsignmentDefaultsCrea, errorOrganizationPoster } =
    useOrganizationPoster()
  const [infoMessage, setInfoMessage] = useState<string>('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    const organizationObj = userData?.organization

    let sellerDelegates: Delegate[]
    let sellerDelegate2Firstname = ''

    let sellerDelegate2Lastname = ''
    let sellerDelegate2Nin = ''

    // Y necesitamos los datos de o de los representantes
    const delegates: Delegate[] = organizationObj?.delegates ?? []

    if (delegates) {
      sellerDelegates = delegates.filter((d) => !d.disabledAt)

      if (sellerDelegates.length >= 1) {
        if (sellerDelegates.length >= 2) {
          sellerDelegate2Firstname = sellerDelegates[1].firstname
          sellerDelegate2Lastname = sellerDelegates[1].lastname
          sellerDelegate2Nin = sellerDelegates[1].nin
        }

        setFormData((prev) => {
          const updatedData = {
            ...prev,
            firstname1: sellerDelegates[0].firstname,
            lastname1: sellerDelegates[0].lastname,
            nin1: sellerDelegates[0].nin,
            firstname2: sellerDelegate2Firstname,
            lastname2: sellerDelegate2Lastname,
            nin2: sellerDelegate2Nin,
          }

          return updatedData
        })
      }
    }
    const articles: Articles | undefined = organizationObj?.articles

    if (articles) {
      setFormData((prev) => {
        const updatedData = {
          ...prev,
          date: getDateFromDb(articles.date),
          notary: articles.notary,
          municipality: articles.municipality,
        }

        return updatedData
      })
    }

    // Load consignment defaults
    const consignmentDefaults = organizationObj?.consignmentDefaults

    if (consignmentDefaults) {
      setFormData((prev) => {
        const updatedData = {
          ...prev,
          consignmentMinimum: consignmentDefaults.minimum ? formatWithThousandSep(consignmentDefaults.minimum) : '',
          consignmentFee: consignmentDefaults.fee ? consignmentDefaults.fee.toString().replace('.', ',') : '',
          consignmentCharge: consignmentDefaults.charge ? formatWithThousandSep(consignmentDefaults.charge) : '',
          consignmentCity: consignmentDefaults.city || '',
        }

        return updatedData
      })
    }
  }, [userData])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setDisabled(false)
  }

  const handleButtonClick = async () => {
    // Clear any previous errors
    setErrors({})

    // Aca guardamos los datos
    const delegates = getDelegates(formData)

    // No validation required - all fields are optional
    const articles: Articles = {
      date: formData.date,
      notary: formData.notary,
      municipality: formData.municipality,
    }
    const resp = await organizationDelegatesCrea(userData?.organization?._id ?? '', delegates, articles)

    if (!resp) {
      setErrorMessage(errorOrganizationPoster?.message ?? 'Error')
    } else if (userData) {
      setUserData({ ...userData, organization: { ...userData.organization!, delegates, articles } })

      setDisabled(true)
      setInfoMessage('Datos guardados.')
    }

    // Save consignment defaults
    const minimum = parseFormattedNumber(formData.consignmentMinimum)
    const fee = parseFloat(formData.consignmentFee.replace(',', '.') || '0')
    const charge = parseFormattedNumber(formData.consignmentCharge)
    const city = formData.consignmentCity.trim()

    await organizationConsignmentDefaultsCrea(
      userData?.organization?._id ?? '',
      isNaN(minimum) ? undefined : minimum,
      isNaN(fee) ? undefined : fee,
      isNaN(charge) ? undefined : charge,
      city || undefined
    )

    if (userData) {
      setUserData({
        ...userData,
        organization: {
          ...userData.organization!,
          consignmentDefaults: {
            minimum: isNaN(minimum) ? undefined : minimum,
            fee: isNaN(fee) ? undefined : fee,
            charge: isNaN(charge) ? undefined : charge,
            city: city || undefined,
          },
        },
      })
    }
  }
  const fields: Array<InputFieldProps> = [
    { label: 'Nombre Representante 1', name: 'firstname1', value: formData.firstname1, type: 'string' },
    { label: 'Apellidos Representante 1', name: 'lastname1', value: formData.lastname1, type: 'string' },
    { label: 'Rut Representante 1', name: 'nin1', value: formData.nin1, type: 'string' },
    { label: 'Nombre Representante 2', name: 'firstname2', value: formData.firstname2, type: 'string' },
    { label: 'Apellidos Representante 2', name: 'lastname2', value: formData.lastname2, type: 'string' },
    { label: 'Rut Representante 2', name: 'nin2', value: formData.nin2, type: 'string' },
  ]
  const fields2: Array<InputFieldProps> = [
    { label: 'Fecha Escritura', name: 'date', value: formData.date, type: 'date' },
    { label: 'Notaría', name: 'notary', value: formData.notary, type: 'string' },
    { label: 'Comuna', name: 'municipality', value: formData.municipality, type: 'string' },
  ]
  const fieldsConsignment: Array<InputFieldProps> = [
    { label: 'Comisión Mínima', name: 'consignmentMinimum', value: formData.consignmentMinimum, type: 'string' },
    { label: 'Porcentaje de Comisión (%)', name: 'consignmentFee', value: formData.consignmentFee, type: 'string' },
    {
      label: 'Cobro por Retiro Anticipado',
      name: 'consignmentCharge',
      value: formData.consignmentCharge,
      type: 'string',
    },
    { label: 'Ciudad', name: 'consignmentCity', value: formData.consignmentCity, type: 'string' },
  ]

  return (
    <div className="mt-8 ml-10 mr-10 ">
      {errorMessage && (
        <div className="text-xl">
          <DisplayError msg={errorMessage} />
        </div>
      )}

      <div className="text-xl">Representante(s) legal(es). </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <FormFields fields={fields} handleInputChange={handleInputChange} errors={errors} />
      </div>

      <div className="text-xl mt-4">Escritura pública</div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <FormFields fields={fields2} handleInputChange={handleInputChange} errors={errors} />
      </div>

      <div className="text-xl mt-8">Valores por Defecto - Contrato de Consignación</div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-2">
        <FormFields fields={fieldsConsignment} handleInputChange={handleInputChange} errors={errors} />
      </div>

      <div className="flex justify-center m-4">
        <ButtonAction label={'Guardar'} handleButtonClick={handleButtonClick} disabled={disabled} />
      </div>
      <div className="flex justify-center mt-4">
        <Info message={infoMessage} trigger={Date.now()} onTimeout={() => setInfoMessage('')} noCenter={true} />
      </div>
    </div>
  )
}

// Obtiene los delegados del formData
// Si el primero esta vacio, mueve el segundo al primero
const getDelegates = (formData: FormData) => {
  const delegates: Delegate[] = []

  if (formData.nin1 || formData.firstname1 || formData.lastname1) {
    const delegate: Delegate = {
      firstname: formData.firstname1,
      lastname: formData.lastname1,
      nin: formData.nin1,
    }
    delegates.push(delegate)
  }

  if (formData.nin2 || formData.firstname2 || formData.lastname2) {
    const delegate: Delegate = {
      firstname: formData.firstname2,
      lastname: formData.lastname2,
      nin: formData.nin2,
    }
    delegates.push(delegate)
  }

  // Si hay dos, y la primera esta vacia, movemos la segunda a la primera
  if (delegates.length >= 2 && isEmpty(delegates[0])) {
    delegates.shift()
  }

  return delegates
}
const isEmpty = (delegate: Delegate) => {
  return (
    delegate.firstname.trim().length === 0 && delegate.lastname.trim().length === 0 && delegate.nin.trim().length === 0
  )
}

const parseFormattedNumber = (formatted: string): number => {
  if (!formatted) return 0
  // Remove all non-digit characters except potential decimal separator
  const cleaned = formatted.replace(/[^\d,.-]/g, '').replace(',', '.')

  return parseFloat(cleaned) || 0
}

export default OrganizationDetail
